/**
 * htmlLegendPlugin — renders a table legend below the doughnut chart.
 *
 * Sort behaviour (value columns only):
 *   first click  → sort descending
 *   second click → sort ascending
 *   third click  → back to canonical order (the order the API returned)
 *
 * Sort state is stored on the chart instance so it survives chart.update() calls
 * without being lost between re-renders.
 */
export const htmlLegendPlugin = {
  id: 'htmlLegend',
  afterUpdate(chart, args, options) {
    const container = document.getElementById(options.containerID)
    if (!container) return

    // Initialise persistent sort state on the chart instance the first time.
    if (!chart.__legendSort) {
      chart.__legendSort = { column: null, dir: null }
    }

    // Clear old legend
    while (container.firstChild) {
      container.firstChild.remove()
    }

    const hasCompare = Boolean(chart.data.datasets[1])

    // --- Cycle helper: null → 'desc' → 'asc' → null
    const cycleSort = (column) => {
      const sort = chart.__legendSort
      if (sort.column !== column) {
        // Clicking a different column always starts at desc.
        chart.__legendSort = { column, dir: 'desc' }
      } else if (sort.dir === 'desc') {
        chart.__legendSort = { column, dir: 'asc' }
      } else {
        // Third click: back to canonical (API) order.
        chart.__legendSort = { column: null, dir: null }
      }
      chart.update()
    }

    // --- Sort indicator text
    const sortIndicator = (column) => {
      const sort = chart.__legendSort
      if (sort.column !== column) return ' ↕'
      return sort.dir === 'asc' ? ' ↑' : ' ↓'
    }

    // Create legend items from the current chart state.
    const rawItems = chart.options.plugins.legend.labels.generateLabels(chart)

    // Apply sort if active.
    const sortedItems = (() => {
      const { column, dir } = chart.__legendSort
      if (!column) return rawItems

      return [...rawItems].sort((first, second) => {
        const datasetIndex = column === 'base' ? 0 : 1
        const firstValue = chart.data.datasets[datasetIndex]?.data[first.index]?.value ?? 0
        const secondValue = chart.data.datasets[datasetIndex]?.data[second.index]?.value ?? 0
        return dir === 'desc' ? secondValue - firstValue : firstValue - secondValue
      })
    })()

    const total_base =
      chart.data.datasets[0]?.data.reduce((sum, item) => sum + item.value, 0) ?? 0
    const total_compare =
      chart.data.datasets[1]?.data.reduce((sum, item) => sum + item.value, 0) ?? 0

    // Build table
    const table = document.createElement('table')
    table.classList.add('table-mini-legend')

    // Header row
    const tr_header = document.createElement('tr')

    const th_empty = document.createElement('th')
    th_empty.setAttribute('colspan', 2)
    tr_header.appendChild(th_empty)

    const th_base = document.createElement('th')
    th_base.style.cursor = 'pointer'
    th_base.style.userSelect = 'none'
    th_base.innerText = (chart.data.datasets[0]?.label ?? '') + sortIndicator('base')
    th_base.addEventListener('click', () => cycleSort('base'))
    tr_header.appendChild(th_base)

    if (hasCompare) {
      const th_compare = document.createElement('th')
      th_compare.style.cursor = 'pointer'
      th_compare.style.userSelect = 'none'
      th_compare.innerText = (chart.data.datasets[1]?.label ?? '') + sortIndicator('compare')
      th_compare.addEventListener('click', () => cycleSort('compare'))
      tr_header.appendChild(th_compare)
    }

    table.appendChild(tr_header)

    // Data rows
    sortedItems.forEach((item) => {
      const tr = document.createElement('tr')
      tr.onclick = () => {
        chart.toggleDataVisibility(item.index)
        chart.update()
      }

      const td = document.createElement('td')
      const box = document.createElement('span')
      box.style.backgroundColor = item.fillStyle
      box.style.borderColor = item.strokeStyle
      box.style.borderWidth = `${item.lineWidth}px`
      td.appendChild(box)
      tr.appendChild(td)

      const td_title = document.createElement('td')
      td_title.innerText = item.text && item.text.length > 0 ? item.text : 'undefined'
      td_title.title = item.text
      tr.appendChild(td_title)

      const td_base = document.createElement('td')
      const span_base_value = document.createElement('span')
      span_base_value.textContent = chart.data.datasets[0]?.data[item.index]?.value
      span_base_value.classList.add('text-gray-700', 'dark:text-gray-300', 'whitespace-nowrap')
      td_base.appendChild(span_base_value)

      const span_base_pc_value = document.createElement('span')
      span_base_pc_value.textContent = `(${Math.round(
        total_base > 0 ? (chart.data.datasets[0]?.data[item.index]?.value / total_base) * 100 : 0,
        1
      )}%)`
      td_base.appendChild(span_base_pc_value)

      if (item.hidden) {
        td_base.classList.add('line-through', 'opacity-50')
      }
      tr.appendChild(td_base)

      if (hasCompare) {
        const td_compare = document.createElement('td')
        const span_compare_value = document.createElement('span')
        span_compare_value.textContent = chart.data.datasets[1]?.data[item.index]?.value ?? 0
        span_compare_value.classList.add(
          'text-gray-700',
          'dark:text-gray-300',
          'whitespace-nowrap'
        )
        td_compare.appendChild(span_compare_value)

        const span_compare_pc_value = document.createElement('span')
        span_compare_pc_value.textContent = `(${Math.round(
          total_compare > 0 && chart.data.datasets[1]?.data[item.index]?.value
            ? (chart.data.datasets[1]?.data[item.index]?.value / total_compare) * 100
            : 0,
          1
        )}%)`
        td_compare.appendChild(span_compare_pc_value)

        if (item.hidden) {
          td_compare.classList.add('line-through', 'opacity-50')
        }
        tr.appendChild(td_compare)
      }

      table.appendChild(tr)
    })

    container.appendChild(table)
  }
}
